import Popover from '@material-ui/core/Popover'
import MailOutlined from '@material-ui/icons/MailOutlined'
import PeopleOutlined from '@material-ui/icons/PeopleOutlined'
import {useMemo, useState} from 'react'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import messages from '../../../assets/messages'
import {PammOfferType} from '../../../graphql/types'
import {formatCreated, formatFunds, formatShare, generateInvestorBook} from '../utils/investorBook'
import {
  CountButton,
  CreatedCell,
  EmailLink,
  FundsCell,
  HeadRow,
  Header,
  HeaderAum,
  HeaderCaption,
  HeaderTitle,
  NameCell,
  PopoverBody,
  Row,
  ScrollArea,
  Table,
  Td,
  Th,
  TotalLabel,
  TotalRow,
} from './InvestorListPopover.styled'

type InvestorListPopoverProps = {
  offer: PammOfferType
} & WithNamespaces

/**
 * The investor-count meta control on an offer card (PT-5705). Clicking it opens a popover,
 * anchored to the card, listing every investor that funds the node — sorted by funds
 * descending (biggest first) with a Funds/Share totals row. The list is deterministic per
 * offer id (see {@link generateInvestorBook}). Esc, an outside click or a re-click close the
 * popover and return focus to the trigger (handled by MUI's Modal/Popover).
 *
 * @param offer - The PAMM offer whose investor book is shown; drives the seed, count and AUM.
 * @param t - Translation function injected by `withNamespaces` (aria-label).
 * @param i18n - i18next instance injected by `withNamespaces`; its language drives number/date locale.
 */
const _InvestorListPopover = ({offer, t, i18n}: InvestorListPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const count = offer.totalInvestments ?? 0
  const aum = offer.totalDepositSum ?? 0
  const currency = offer.currency ?? 'USD'
  const locale = i18n?.language

  const investors = useMemo(
    () => generateInvestorBook({offerId: offer.id ?? 0, totalInvestors: count, aum}),
    [offer.id, count, aum],
  )

  const trigger = (
    <CountButton
      type="button"
      disabled={investors.length === 0}
      onClick={(event) => setAnchorEl(event.currentTarget)}
      aria-label={t(messages.pammInvestorBookAria.i18nKey, messages.pammInvestorBookAria.defaults)}
      aria-haspopup="dialog"
      aria-expanded={Boolean(anchorEl)}
    >
      <PeopleOutlined style={{fontSize: 14}} />
      {/* `investorsCount` (not `count`): `count` is an i18next reserved keyword that triggers
          plural-key resolution and leaves the number blank. */}
      <Trans {...messages.pammInvestorsCount} values={{investorsCount: count}} />
    </CountButton>
  )

  if (investors.length === 0) return trigger

  const totalFunds = investors.reduce((sum, investor) => sum + investor.funds, 0)
  const totalShare = investors.reduce((sum, investor) => sum + investor.share, 0)

  return (
    <>
      {trigger}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        PaperProps={{
          role: 'dialog',
          'aria-label': t(messages.pammInvestorBookTitle.i18nKey, messages.pammInvestorBookTitle.defaults),
          style: {overflow: 'hidden', borderRadius: 12},
        }}
      >
        <PopoverBody>
          <Header>
            <div>
              <HeaderCaption>
                <Trans {...messages.pammInvestorBookTitle} />
              </HeaderCaption>
              <HeaderTitle>
                <Trans
                  {...messages.pammInvestorBookSubtitle}
                  values={{owner: offer.name, investorsTotal: investors.length}}
                />
              </HeaderTitle>
            </div>
            <div>
              <HeaderCaption>
                <Trans {...messages.pammInvestorBookAum} />
              </HeaderCaption>
              <HeaderAum>{formatFunds(aum, currency, locale)}</HeaderAum>
            </div>
          </Header>

          <ScrollArea>
            <Table>
              <thead>
                <HeadRow>
                  <Th>
                    <Trans {...messages.pammInvestorBookColInvestor} />
                  </Th>
                  <Th align="right">
                    <Trans {...messages.pammInvestorBookColFunds} />
                  </Th>
                  <Th align="right">
                    <Trans {...messages.pammInvestorBookColShare} />
                  </Th>
                  <Th>
                    <Trans {...messages.pammInvestorBookColCreated} />
                  </Th>
                  <Th>
                    <Trans {...messages.pammInvestorBookColEmail} />
                  </Th>
                </HeadRow>
              </thead>
              <tbody>
                {investors.map((investor) => (
                  <Row key={investor.id}>
                    <NameCell>{investor.name}</NameCell>
                    <FundsCell align="right">{formatFunds(investor.funds, currency, locale)}</FundsCell>
                    <Td align="right">{formatShare(investor.share)}</Td>
                    <CreatedCell>{formatCreated(investor.created, locale)}</CreatedCell>
                    <Td>
                      <EmailLink href={`mailto:${investor.email}`}>
                        <MailOutlined style={{fontSize: 13}} />
                        {investor.email}
                      </EmailLink>
                    </Td>
                  </Row>
                ))}
              </tbody>
              <tfoot>
                <TotalRow>
                  <TotalLabel>
                    <Trans {...messages.pammInvestorBookTotal} />
                  </TotalLabel>
                  <td style={{textAlign: 'right'}}>{formatFunds(totalFunds, currency, locale)}</td>
                  <td style={{textAlign: 'right'}}>{formatShare(totalShare)}</td>
                  <td colSpan={2} />
                </TotalRow>
              </tfoot>
            </Table>
          </ScrollArea>
        </PopoverBody>
      </Popover>
    </>
  )
}

export const InvestorListPopover = withNamespaces()(_InvestorListPopover)
